<template>
  <div class="dropdown searchable-dropdown position-relative user-select-none">
    <a class="dropdown-toggle link" data-toggle="dropdown">
      {{ appTranslate('Choose fields') }}

      <template v-if="enabledTemplates.length">
        <span>
          <small
            v-for="(template, i) of enabledTemplates"
            :key="i"
            class="enabled-template"
            :class="{ 'field-missing': !fullySelectedTemplates.has(template.id) }"
          >
            {{ template.name }}
          </small>
        </span>
        <small v-if="additionallySelectedPropertiesSum"> + {{ additionallySelectedPropertiesSum }}</small>
      </template>
      <small v-else>
        ({{ allPropertiesAreSelected ? appTranslate('All') : verifiedSelectedPropertyKeys.size }} {{ appTranslate('selected', undefined, 'lowercase') }})
      </small>

      <div class="deadlines">
        <span
          v-for="item of propertyStore.deadlines?.levels"
          :key="item.level"
          class="info-circle font-normal"
          :class="{ 'inactive icon-disabled': !selectedDeadlinesModel[item.level] }"
          :style="`background-color: ${item.colour};`"
        >
          {{ item.level }}
        </span>
        <span class="info-circle empty" :class="{ 'inactive icon-disabled': !selectedDeadlinesModel.untagged }" />
      </div>
    </a>

    <div class="dropdown-menu down minimalistic" @click.stop>
      <ToggleComponent v-model="currentMode" :options="modes" class="m-2" style="justify-content: space-around" />

      <!-- Profiles section -->
      <section v-if="currentMode === 'profiles'">
        <div class="p-2">
          <InfoPopupComponent class="ml-1" content-style="max-width: 350px; right: 5px; z-index: 1021">
            {{ appTranslate('Applying a template does not remove any selected fields') }}.
            {{ appTranslate("Deselecting a template removes fields from that template that aren't present in other selected templates") }}. <br /><br />
            {{
              appTranslate(
                'A checkbox with blue background without checkmark indicates the template is loaded, but one or more fields from it have been hidden',
              )
            }}
          </InfoPopupComponent>

          <small>
            {{ appTranslate('You can apply several templates at the same time') }}
          </small>

          <div class="mt-2 d-flex flex-wrap">
            <button
              class="col transparent"
              :disabled="allSelectedTemplateFieldsAreShown && !additionallySelectedPropertiesSum"
              @click="restoreSelectedTemplates()"
            >
              {{ appTranslate('Restore selected templates') }}
            </button>
            <div class="spacer" />
            <button class="col transparent" :disabled="!enabledTemplates.length" @click="removeAllSelectedTemplates()">
              {{ appTranslate('Deselect all') }}
            </button>
          </div>

          <hr />

          <small> {{ appTranslate('Public profiles') }} </small>

          <!-- Select profiles -->
          <div class="templates mt-2">
            <PropertyProfilesComponent
              v-if="store.templates.properties.public"
              :templates="store.templates.properties.public"
              :enabled-templates-ids="enabledTemplatesIds"
              :fully-selected-templates="fullySelectedPublicTemplates"
              @toggle="togglePropertiesTemplate"
            />
          </div>

          <small v-if="additionallySelectedPropertiesSum" class="additional-text mt-1">
            {{ appTranslate('%s additional field(s) selected', [additionallySelectedPropertiesSum]) }}
          </small>
        </div>

        <div class="p-2 border-top private-profiles">
          <template v-if="store.templates.properties.private?.length">
            <small>{{ appTranslate('Private profiles') }}</small>

            <div class="templates mt-2">
              <PropertyProfilesComponent
                v-if="store.templates.properties.private?.length"
                :templates="store.templates.properties.private"
                :enabled-templates-ids="enabledTemplatesIds"
                :fully-selected-templates="fullySelectedPrivateTemplates"
                deletable
                @toggle="togglePropertiesTemplate"
                @delete="deletePropertiesTemplate"
              />
            </div>
          </template>

          <datalist id="private-profiles-list">
            <option v-for="(template, i) of store.templates.properties.private" :key="i">{{ template.name }}</option>
          </datalist>

          <small> {{ appTranslate('Save selected field(s) to a private profile') }} </small>

          <div class="d-flex gap-2">
            <input
              v-model="stagedPrivateTemplateName"
              type="text"
              class="form-control"
              :placeholder="appTranslate('Enter a name')"
              list="private-profiles-list"
            />
            <button class="btn btn-primary" :disabled="!stagedPrivateTemplateName" @click="savePropertiesTemplate(stagedPrivateTemplateName)">
              {{ appTranslate('Save') }}
            </button>
          </div>
        </div>
      </section>

      <!-- Individual properties -->
      <section v-if="currentMode === 'fields'" class="px-2 pb-2 pt-0">
        <div class="bg-white sticky-top pt-2">
          <input
            v-model="propertySearch"
            type="search"
            class="form-control mt-2 search"
            :placeholder="appTranslate('Search')"
            @keydown.esc="propertySearch ? $event.stopPropagation() : 0"
            @keydown.stop
          />

          <!-- Select/deselect-all buttons -->
          <div class="d-flex">
            <button class="col transparent" @click="toggleVisibleProperties(true)">
              {{ appTranslate(propertySearch ? 'Select visible (%s)' : 'Select all', [filteredNonCulturalProperties.length]) }}
            </button>
            <div class="spacer" />
            <button class="col transparent" @click="toggleVisibleProperties(false)">
              {{ appTranslate(propertySearch ? 'Deselect visible (%s)' : 'Deselect all', [filteredNonCulturalProperties.length]) }}
            </button>
          </div>

          <small class="additional-text mt-1 mb-2">
            {{ appTranslate('%s fields selected', [verifiedSelectedPropertyKeys.size]) }}
          </small>
        </div>

        <!-- Properties list -->
        <div class="properties-list">
          <label
            v-for="(property, i) of filteredNonCulturalProperties"
            :key="i"
            class="item"
            :class="{
              included: propertyKeysFromEnabledTemplates.has(property.key) && !forcedPropertyKeys.has(property.key),
              'text-disabled': !propertyKeysIncludedInSelectedDeadlines?.has(property.key),
            }"
            :title="
              propertyKeysIncludedInSelectedDeadlines?.has(property.key) ? undefined : appTranslate('Field hidden in accordance with your deadlines filter')
            "
          >
            <input v-model="selectedPropertyKeysLocal[property.key]" type="checkbox" :disabled="forcedPropertyKeys.has(property.key)" @change="emitUpdate()" />
            {{ property.header }}
            <div v-if="['languageSpecific', 'countrySpecific'].includes(property.type)" class="w-100 mb-1">
              <CultureSpecificAttribute
                v-for="country of store.availableCountries"
                :key="country"
                :country="country"
                class="tiny-flag"
                :class="{ disabled: !selectedCountries.has(country) }"
              />
            </div>
            <CultureSpecificAttribute
              v-for="country of property.exclusiveToCountries"
              :key="country"
              :country="country"
              class="tiny-flag"
              :class="{ disabled: !selectedCountries.has(country) }"
            />
          </label>
        </div>
      </section>

      <section v-if="currentMode === 'deadlines'" class="p-2">
        <InfoPopupComponent content-style="max-width: 90%">
          {{
            appTranslate(
              "Filters fields by which deadline category they're tagged with (if any). This filter is applied on top of your selected profile(s)/individual field filters",
            )
          }}
        </InfoPopupComponent>
        <small>{{ appTranslate('Filter fields by deadline') }} </small>

        <div v-for="(item, i) of propertyStore.deadlines?.levels" :key="i" class="deadline" :style="`color: ${item.colour}`">
          <label>
            <input v-model="selectedDeadlinesModel[item.level]" type="checkbox" class="mr-1" />
            <strong>
              {{ appTranslate('Deadline') }}
              <span class="info-circle" :style="`background-color: ${item.colour}`">{{ item.level }}</span>
            </strong>
          </label>
          <div class="info">
            {{ item.info }}
          </div>
        </div>

        <div class="deadline">
          <label>
            <input v-model="selectedDeadlinesModel.untagged" type="checkbox" class="mr-1" />
            <strong>
              {{ appTranslate('Fields not tagged with a deadline') }}
              <!-- {{ appTranslate('No deadline') }} -->
              <span class="info-circle empty" />
            </strong>
          </label>
          <div class="info"></div>
        </div>

        <label class="mt-4">
          <span>{{ appTranslate('Colour headers by deadline') }} </span>
          <select v-model="colouredPropertyDeadlineHeaders" class="form-control">
            <option v-for="visibility of PropertyDeadlineVisibilities" :key="visibility" :value="visibility">
              {{ appTranslate(visibility) }}
            </option>
            <option :value="undefined">
              {{ appTranslate('Never') }}
            </option>
          </select>
        </label>
      </section>
    </div>
  </div>
</template>

<script lang="ts" setup>
import CultureSpecificAttribute from '@/components/simple/CultureSpecificAttribute.vue'
import InfoPopupComponent from '@/components/simple/InfoPopupComponent.vue'
import ToggleComponent from '@/components/ToggleComponent.vue'
import { useTranslation } from '@/composables/useTranslation'
import { BooleanObject } from '@/models/GenericInterfaces'
import { Culture, Option } from '@/models/Global'
import Message from '@/models/Message'
import {
  AdvancedProperty,
  EditableAdvancedProperty,
  PropertyDeadlineVisibilities,
  PropertyDeadlineVisibility,
  PropertyTemplate,
  SelectedPropertyRecord,
} from '@/models/Property'
import DataService from '@/services/DataService'
import { useDefaultStore } from '@/store'
import { usePropertyStore } from '@/store/propertyStore'
import { browserStorage } from '@/utils/browserStorage'
import { getPropertyKey } from '@/utils/propertyUtils.js'
import { areComparable, arrayToSet, toComparable } from '@/utils/tools'
import { computed, onActivated, onMounted, reactive, ref, watch } from 'vue'
import PropertyProfilesComponent from '../properties/PropertyProfilesComponent.vue'

// -----------
// -- Setup --
// -----------
const props = defineProps<{
  properties: EditableAdvancedProperty[]
  modelValue: BooleanObject
  selectedCultures?: Culture[]
  view: 'productTable' | 'product'
  forcedProperties?: AdvancedProperty[]
  editMode?: boolean
  selectedDeadlines: SelectedPropertyRecord
  propertyKeysIncludedInSelectedDeadlines?: Set<string>
}>()

const emit = defineEmits(['update:modelValue', 'update:selectedDeadlines'])
const store = useDefaultStore()
const propertyStore = usePropertyStore()
const { appTranslate } = useTranslation()

// ----------------------------------------------------
// -- Functions used by lifecycle and computed props --
// ----------------------------------------------------

const getFullySelectedTemplates = (templates: PropertyTemplate[]) =>
  templates?.reduce<Set<string>>((acc, template) => {
    const allPropertiesSelected = template.mappings.every((mapping) => {
      const mappingKey = getPropertyKey(mapping)
      return verifiedSelectedPropertyKeys.value.has(mappingKey) || !nonCulturalPropertyKeysSet.value.has(mappingKey)
    })

    if (allPropertiesSelected) {
      acc.add(template.id)
    }

    return acc
  }, new Set())

const getEnabledTemplateIds = () => browserStorage.get<string>(EnabledTemplateStorageKey)

// -------------
// -- Statics --
// -------------
const SelectedPropertiesStorageKey = `${props.view}-selectedProperties` as const
const EnabledTemplateStorageKey = `${props.view}-enabledTemplates` as const
const ShowColouredDeadlineHeadersStorageKey = `${props.view}-showColouredDeadlineHeaders` as const
const SelectedPropertyDeadlinesStorageKey = `${props.view}-selectedPropertyDeadlines` as const

type Mode = 'profiles' | 'fields' | 'deadlines' | 'cultures'

const modes: Option<Mode>[] = [
  {
    displayValue: appTranslate('Profiles'),
    value: 'profiles',
  },
  {
    displayValue: appTranslate('Individual fields'),
    value: 'fields',
  },
  {
    displayValue: appTranslate('Deadlines'),
    value: 'deadlines',
  },
  // {
  //   displayValue: appTranslate('Cultures'),
  //   value: 'cultures',
  // },
]

const allDeadlines = { 1: true, 2: true, 3: true, untagged: true } as const

// ---------------
// -- Reactives --
// ---------------
const propertySearch = ref('')
const selectedPropertyKeysLocal = ref<BooleanObject>({})
const enabledTemplatesIds = reactive(new Set(getEnabledTemplateIds()))
const currentMode = ref<Mode>(modes[0].value)
const colouredPropertyDeadlineHeaders = ref<PropertyDeadlineVisibility>()

const stagedPrivateTemplateName = ref('')

// --------------------
// -- Computed props --
// --------------------
const forcedPropertyKeys = computed(() => {
  const set = new Set<string>()
  props.forcedProperties?.forEach((property) => set.add(property.key))
  return set
})

const nonCulturalProperties = computed(() => props.properties?.filter((prop) => !prop.culturePrefix))
const nonCulturalPropertyKeysSet = computed(() => arrayToSet(nonCulturalProperties.value, 'key'))

const filteredNonCulturalProperties = computed(() => {
  const searchTerm = toComparable(propertySearch.value)
  return nonCulturalProperties.value?.reduce<EditableAdvancedProperty[]>((acc, prop) => {
    if (!searchTerm || toComparable(prop.header).includes(searchTerm) || toComparable(prop.defaultName).includes(searchTerm)) {
      acc.push(prop)
    }
    return acc
  }, [])
})

/** includes forced properties */
const verifiedSelectedPropertyKeys = computed(() => {
  const set = new Set(forcedPropertyKeys.value)

  return Object.entries(selectedPropertyKeysLocal.value).reduce((acc, [key, enabled]) => {
    if (enabled && nonCulturalPropertyKeysSet.value.has(key)) {
      acc.add(key)
    }
    return acc
  }, set)
})

/** includes forced properties */
const propertyKeysFromEnabledTemplates = computed(() => {
  const set = new Set(forcedPropertyKeys.value)

  return enabledTemplates.value.reduce((acc, template) => {
    for (const mapping of template.mappings) {
      const key = getPropertyKey(mapping)
      if (nonCulturalPropertyKeysSet.value.has(key)) {
        acc.add(key)
      }
    }
    return acc
  }, set)
})

/** properties not from templates */
const additionallySelectedPropertiesSum = computed(() => {
  let extra = 0
  for (const propertyKey of verifiedSelectedPropertyKeys.value) {
    if (!propertyKeysFromEnabledTemplates.value.has(propertyKey)) {
      extra++
    }
  }

  return extra
})

const allPropertiesAreSelected = computed(
  () =>
    verifiedSelectedPropertyKeys.value.size >= nonCulturalProperties.value.length &&
    nonCulturalProperties.value.every((property) => verifiedSelectedPropertyKeys.value.has(property.key)),
)

const propertyKeysFromTemplates = computed(() => Array.from(new Set(enabledTemplates.value.flatMap((template) => template.mappings.map(getPropertyKey)))))

const allSelectedTemplateFieldsAreShown = computed(() =>
  propertyKeysFromTemplates.value.every((comparable) => verifiedSelectedPropertyKeys.value.has(comparable)),
)

const enabledPublicTemplates = computed(() => store.templates.properties.public?.filter((t) => enabledTemplatesIds.has(t.id)) || [])
const enabledPrivateTemplates = computed(() => store.templates.properties.private?.filter((t) => enabledTemplatesIds.has(t.id)) || [])
const enabledTemplates = computed(() => [...enabledPublicTemplates.value, ...enabledPrivateTemplates.value])

const fullySelectedPublicTemplates = computed(() => getFullySelectedTemplates(enabledPublicTemplates.value))
const fullySelectedPrivateTemplates = computed(() => getFullySelectedTemplates(enabledPrivateTemplates.value))
const fullySelectedTemplates = computed(() => new Set([...fullySelectedPublicTemplates.value, ...fullySelectedPrivateTemplates.value]))

const selectedCountries = computed(() => new Set(props.selectedCultures?.map((culture) => culture.country)))

const selectedDeadlinesModel = computed({
  get: () => props.selectedDeadlines,
  set: (value) => {
    emit('update:selectedDeadlines', props.selectedDeadlines)
  },
})

// --------------
// -- Watchers --
// --------------
watch(
  () => props.properties,
  () => loadSelectedFields(),
)

watch(
  () => props.modelValue,
  (newValue) => {
    // also saves to localStorage when columns are hidden from outside component through modelValue prop
    browserStorage.set(SelectedPropertiesStorageKey, newValue)
  },
  {
    deep: true,
  },
)

watch(
  () => props.editMode,
  () => {
    updateShowColouredDeadlinesHeaders()
  },
)

watch(
  () => colouredPropertyDeadlineHeaders.value,
  (value) => {
    if (value) {
      browserStorage.set(ShowColouredDeadlineHeadersStorageKey, value)
    } else {
      browserStorage.delete(ShowColouredDeadlineHeadersStorageKey)
    }
    updateShowColouredDeadlinesHeaders()
  },
)

watch(
  () => props.selectedDeadlines,
  () => {
    browserStorage.set(SelectedPropertyDeadlinesStorageKey, props.selectedDeadlines)
  },
  { deep: true },
)

// ---------------------
// -- Lifecycle hooks --
// ---------------------
onMounted(() => {
  DataService.loadTemplates('properties', 'public')
  DataService.loadTemplates('properties', 'private')

  loadSelectedFields() // needed for hot reloads

  colouredPropertyDeadlineHeaders.value = browserStorage.get<PropertyDeadlineVisibility>(ShowColouredDeadlineHeadersStorageKey)
  updateShowColouredDeadlinesHeaders()
  emit('update:selectedDeadlines', browserStorage.get(SelectedPropertyDeadlinesStorageKey) || allDeadlines)
})

onActivated(() => {
  updateShowColouredDeadlinesHeaders()
})

// ---------------
// -- Functions --
// ---------------

const updateShowColouredDeadlinesHeaders = () => {
  const value = colouredPropertyDeadlineHeaders.value
  propertyStore.showColouredDeadlineHeaders = value === 'always' || (colouredPropertyDeadlineHeaders.value === 'in edit mode' && props.editMode)
}

/** Toggles all properties on or all off (except forced) */
const selectAllProperties = (state: boolean) => {
  if (state) {
    selectedPropertyKeysLocal.value =
      nonCulturalProperties.value?.reduce<BooleanObject>((acc, property) => {
        acc[property.key] = true
        return acc
      }, {}) || {}
  } else {
    selectedPropertyKeysLocal.value = setToBooleanObject(forcedPropertyKeys.value)
  }

  emitUpdate()
}

const setToBooleanObject = (set: Set<string>) => {
  const obj: BooleanObject = {}
  for (const propertyKey of set) {
    obj[propertyKey] = true
  }
  return obj
}

const toggleVisibleProperties = (state: boolean) => {
  if (!propertySearch.value) {
    selectedPropertyKeysLocal.value = {}
  }

  filteredNonCulturalProperties.value?.forEach((property) => {
    if (state || forcedPropertyKeys.value.has(property.key)) {
      selectedPropertyKeysLocal.value[property.key] = true
    } else {
      delete selectedPropertyKeysLocal.value[property.key]
    }
  })

  emitUpdate()
}

const togglePropertiesTemplate = (template: PropertyTemplate) => {
  if (enabledTemplatesIds.has(template.id)) {
    unloadPropertiesTemplate(template)
  } else {
    loadPropertiesTemplate(template)
  }
}

const loadPropertiesTemplate = (template: PropertyTemplate) => {
  // check for "broken" properties in the template

  template.mappings.forEach((mapping) => {
    const mappingKey = getPropertyKey(mapping)

    if (!nonCulturalPropertyKeysSet.value?.has(mappingKey) && verifiedSelectedPropertyKeys.value.has(mappingKey)) {
      console.log(`Could not find prop ${mapping.defaultName}`)
    }

    selectedPropertyKeysLocal.value[mappingKey] = true
  })
  if (template.id) {
    enabledTemplatesIds.add(template.id)
  }
  saveEnabledTemplateIds()
  emitUpdate()
}

const unloadPropertiesTemplate = (unloadTemplate: PropertyTemplate) => {
  const enabledFieldsFromOtherLoadedTemplates = new Set(
    enabledTemplates.value.reduce<string[]>((acc, template) => {
      if (unloadTemplate.id !== template.id) {
        for (const mapping of template.mappings) {
          acc.push(getPropertyKey(mapping))
        }
      }

      return acc
    }, []),
  )

  unloadTemplate.mappings.forEach((mapping) => {
    const propertyKey = getPropertyKey(mapping)
    if (!enabledFieldsFromOtherLoadedTemplates.has(propertyKey)) {
      delete selectedPropertyKeysLocal.value[propertyKey]
    }
  })
  enabledTemplatesIds.delete(unloadTemplate.id)
  saveEnabledTemplateIds()
  emitUpdate()
}

const saveEnabledTemplateIds = () => {
  browserStorage.set(EnabledTemplateStorageKey, enabledTemplatesIds)
}

const emitUpdate = () => {
  emit('update:modelValue', selectedPropertyKeysLocal.value)
}

const restoreSelectedTemplates = () => {
  selectedPropertyKeysLocal.value = setToBooleanObject(forcedPropertyKeys.value)
  enabledTemplates.value.forEach((template) => loadPropertiesTemplate(template))
  !enabledTemplates.value.length && emitUpdate()
}

const removeAllSelectedTemplates = () => {
  enabledTemplates.value.forEach((template) => unloadPropertiesTemplate(template))
}

const loadSelectedFields = () => {
  const failCallback = () => {
    browserStorage.delete(SelectedPropertiesStorageKey)
    store.addMessage(new Message('Error loading properties. Loading all', undefined, 'warning'))
  }

  const stored = browserStorage.get(SelectedPropertiesStorageKey, undefined, failCallback)

  if (!stored) {
    selectAllProperties(true)
    return
  }

  // convert legacy attribute keys (non id keys) to id keys
  const storedSet = new Set(
    Object.entries(stored)
      .filter(([, enabled]) => enabled)
      .map(([key]) => key),
  )

  selectedPropertyKeysLocal.value = nonCulturalProperties.value.reduce<BooleanObject>((acc, property) => {
    const comparable = toComparable(property.defaultName)

    // convert legacy attribute keys to ids
    if (property.id && (storedSet.has(property.id) || storedSet.has(comparable))) {
      acc[property.id] = true
    } else if (storedSet.has(comparable)) {
      acc[comparable] = true
    }
    return acc
  }, {})

  // set default props to visible
  for (const propertyKey in forcedPropertyKeys.value) {
    selectedPropertyKeysLocal.value[propertyKey] = true
  }

  emitUpdate()
}

const savePropertiesTemplate = async (name: string, visibility: 'private' = 'private') => {
  const existingTemplate = store.templates.properties?.[visibility]?.find((template) => areComparable(template.name, name))

  if (!existingTemplate || confirm(appTranslate(`A profile called "${name}" already exists. Do you want to overwrite it?`))) {
    const selectedProperties = nonCulturalProperties.value.filter((property) => {
      // filter out default properties
      return verifiedSelectedPropertyKeys.value.has(property.key) && !forcedPropertyKeys.value.has(property.key)
    })
    const mappings = selectedProperties.map((property) => ({ defaultName: property.defaultName, id: property.id }))
    await DataService.saveTemplate('properties', { name, mappings }, visibility, existingTemplate?.id, false)
  }
}

const deletePropertiesTemplate = async (template: PropertyTemplate, visibility: 'private' = 'private') => {
  if (confirm(appTranslate(`Do you want to delete the profile "%s"?`, [template.name]))) {
    await DataService.removeTemplate('properties', template.id, visibility)
  }
}
</script>

<style lang="scss" scoped>
.enabled-template {
  color: #0c0;

  font-weight: bold;

  &:not(:last-child)::after {
    content: ', ';
  }

  &.field-missing {
    color: red;
  }
}

.dropdown-toggle {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  display: block;
}

.searchable-dropdown {
  .dropdown-menu {
    overflow-y: scroll;
    width: 400px; //500px;
    height: 400px;
    padding: 0;

    section {
      label.item {
        display: flex;
        align-items: center;
        flex-wrap: wrap;
        column-gap: 0.25rem;
      }
    }

    button.transparent {
      border: none;
      background-color: transparent;
      font-size: 0.9em;

      outline: none;
      padding: 0.6em;

      &:hover:not(:disabled) {
        background-color: #ddd;
      }
    }

    .additional-text {
      color: grey;
      display: block;
    }

    .templates {
      columns: 2;
      padding: 0.25em;
    }

    .spacer {
      height: inherit;
      width: 1px;
      background-color: var(--light-grey);
    }

    .private-profiles {
      background-color: #eee;
    }

    .properties-list {
      .item {
        width: 100%;
        padding: 0.25em;
        margin-bottom: 0;

        .tiny-flag {
          margin-left: 1px;
        }

        &:hover {
          background-color: #eee;
        }

        &.included {
          color: blue;
        }

        input[type='checkbox'] {
          height: 1em;
          width: 1em;
        }
      }
    }
  }
}

.deadlines {
  display: inline-flex;
  margin-left: 0.25rem;
  gap: 2px;
}

.deadline {
  display: block;

  label {
    margin-bottom: 0.25em;
  }

  .info {
    text-indent: 1em;
  }
}

.info-circle {
  margin-right: 1px;

  &.empty {
    vertical-align: -0.2em;
    background-color: black;
  }
}
</style>
